import React, { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  PopoverOrigin,
  Select,
  SelectProps,
  ListSubheader,
  TextField,
  InputAdornment,
  IconButton,
  SvgIcon,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { useStyles, anchorOriginDefault } from "./SelectBox.styles";
import { ReactComponent as downArrowIcon } from "./../../assets/icons/down-arrow.svg";
import AppConstants from "../../constants";
import { ReactComponent as searchIcon } from "./../../assets/icons/search-icon-small.svg";
import { isEqualArrays } from "utils/helpers.utils";

export interface SelectItemProps {
  name?: string;
  value: any;
  countryCode?: any;
}

interface SelectBoxProps extends SelectProps {
  handleChange?: (value: any) => void;
  handleFormikChange?: (e: React.ChangeEvent<any>) => void;
  items: Array<SelectItemProps>;
  helperText?: any;
  hideIconComponent?: boolean;
  menuPropsList?: string;
  menuPaperProps?: string;
  anchorOrigin?: PopoverOrigin | undefined;
  reset?: boolean;
  resetAll?: boolean;
  showSelectNone?: boolean;
  enableSearch?: boolean;
  onSearch?: (searchText: string) => void; // prop for API search
  onScrollEnd?: () => void; // prop for infinite scroll
  classes?: any;
  customWidthSearchBar?: boolean;
  disabled?: boolean;
  loading?: boolean; // prop to control loading state
  preserveSearchText?: boolean; // prop to preserve search text
}
const SelectBox = (props: SelectBoxProps) => {
  const classes = useStyles();
  const {
    handleChange,
    handleFormikChange,
    value,
    multiple,
    id,
    label,
    items,
    hideIconComponent,
    menuPaperProps,
    menuPropsList,
    anchorOrigin,
    className,
    reset,
    resetAll,
    showSelectNone,
    error,
    helperText,
    fullWidth,
    enableSearch,
    onSearch, // <-- Destructure new prop
    onScrollEnd, // <-- Destructure new prop
    customWidthSearchBar,
    disabled=false,
    loading=false,
    preserveSearchText=false, // prop to preserve search text
    ...rest
  } = props;
  const [itemsDdn, setItemsDdn] = useState(items && items.map((item) => item.value));
  const [itemsPrevValues, setItemsPrevValues] = useState<SelectItemProps[]>([]);
  const [ddnValue, setDdnValue] = useState<any>(value || AppConstants.SELECT_NONE.value);
  const anchorEl = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Add a ref to the menu list for scroll event
  const menuListRef = useRef<any>(null);

  const setMenuListRef = useCallback((node: any) => {
    if (node) {
      menuListRef.current = node;
    }
  }, []);

  // Infinite scroll handler
  useEffect(() => {
    if (!isOpen || !props.onScrollEnd) return;
    const menuList = menuListRef.current;
    if (!menuList) return;
  
    const handleScroll = (e: any) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        onScrollEnd && onScrollEnd();
      }
    };
    menuList.addEventListener('scroll', handleScroll);
    return () => menuList.removeEventListener('scroll', handleScroll);
  }, [isOpen, props.onScrollEnd]);

  const containsText = (text: any, searchText: any) => {
    return text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
  };

  const displayedOptions = useMemo(() => {
    if (onSearch) return items; // If using API search, don't filter locally
    return items.filter((option) => containsText(option.name, searchText));
  }, [searchText, items, onSearch]);

  const getMenuItems = () => {
    const result = searchText ? displayedOptions : items;
    return result && result.length
      ? result.map((item, index) => (
          <MenuItem key={`${item.value}-${index}`} value={item.value}>
            {multiple && <Checkbox size="small" color="primary" checked={ddnValue.indexOf(item.value) > -1} />}
            <ListItemText primary={item.name} />
          </MenuItem>
        ))
      : null;
  };

  const handleDdnChange = useCallback(
    (event: React.ChangeEvent<SelectItemProps>, child: React.ReactNode) => {
      let selected = event.target.value;

      if (multiple) {
        const selectAllIdx = selected.indexOf(AppConstants.SELECT_ALL.value);
        const selectAllPreviousIdx = ddnValue.indexOf(AppConstants.SELECT_ALL.value);
        const selectNoneIdx = selected.indexOf(AppConstants.SELECT_NONE.value);
        if (selectNoneIdx > -1) {
          selected = selected.filter((item: any) => item !== AppConstants.SELECT_NONE.value);
        }
        if (selectAllIdx > -1) {
          if (selected.length < itemsDdn.length) {
            if (selectAllPreviousIdx > -1) {
              selected = selected.filter((item: any) => item !== AppConstants.SELECT_ALL.value);
            } else {
              selected = itemsDdn.slice();
            }
          }
        } else {
          if (selected.length < itemsDdn.length) {
            if (selectAllPreviousIdx > -1) {
              selected = [];
            } else if (selected.length === itemsDdn.length - 1) {
              selected = itemsDdn.slice();
            }
          }
        }
        if (!selected.length) {
          selected = [AppConstants.SELECT_NONE.value];
        }
      }

      if (Array.isArray(selected)) {
         selected = selected.filter((item: any) => item);
        if (!selected.includes(undefined)) {
          event.target.value = selected;
          setDdnValue(selected);
          handleChange && handleChange(selected);
          handleFormikChange && handleFormikChange(event);
        }
      } else {
        event.target.value = selected;
        setDdnValue(selected);
        handleChange && handleChange(selected);
        handleFormikChange && handleFormikChange(event);
      }
    },
    [ddnValue, itemsDdn, multiple, handleChange, handleFormikChange]
  );

  useEffect(() => {
    if (!isEqualArrays(items, itemsPrevValues, "value")) {
      if (multiple) {
        const allValues = [AppConstants.SELECT_ALL.value, ...items.map((item) => item.value)];
        setItemsDdn(allValues);
        // If a real selection was already provided (e.g. editing an existing record whose
        // value arrives before `items` finishes loading asynchronously), keep it instead of
        // clobbering it with "select all" once `items` populates. This includes an explicit
        // SELECT_NONE selection, which is itself a real, intentional value. A value that is
        // just the SELECT_ALL sentinel (e.g. default filter state `["all"]`) is not a real
        // selection of item values, so it must still expand to `allValues` - otherwise the
        // "Select All" checkbox shows checked while individual item checkboxes don't.
        const includesSelectAll = Array.isArray(value) && value.indexOf(AppConstants.SELECT_ALL.value) > -1;
        const hasExistingSelection = Array.isArray(value) && value.length > 0 && !includesSelectAll;
        setDdnValue(hasExistingSelection ? value : allValues);
      } else {
        setDdnValue(value);
        showSelectNone && setItemsDdn([AppConstants.SELECT_NONE.value, ...items.map((item) => item.value)]);
      }
    }
    setItemsPrevValues(items);
  }, [items]);

  useEffect(() => {
    if (reset) {
      if (multiple) {
        setDdnValue(itemsDdn);
        handleChange && handleChange(itemsDdn);
      } else {
        setDdnValue(value);
        handleChange && handleChange(value);
      }
    } else if (multiple && Array.isArray(value) && value.indexOf(AppConstants.SELECT_ALL.value) > -1) {
      // Expand the bare SELECT_ALL sentinel here too, deriving it from the `items` prop
      // directly rather than the `itemsDdn` state. This effect and the items effect above
      // both run on mount and both call `setDdnValue`; whichever is declared later used to
      // win outright, so a remount with `items` already populated (e.g. navigating back to
      // a page whose data is still cached in Redux) left `ddnValue` as the raw `["all"]`
      // sentinel - "Select All" checked, every individual item unchecked. Computing the same
      // expansion from `items` in both effects makes the end result consistent regardless of
      // which one runs last, instead of just deferring the race to a different prop.
      setDdnValue([AppConstants.SELECT_ALL.value, ...items.map((item) => item.value)]);
    } else {
      setDdnValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (resetAll) {
      if (multiple) {
        setDdnValue(itemsDdn);
        handleChange && handleChange(itemsDdn);
      } else {
        setDdnValue(value);
        handleChange && handleChange(itemsDdn);
      }
    }
  }, [resetAll]);

  const getChips = (selected: any) => {
    if (multiple) {
      if (selected.indexOf(AppConstants.SELECT_ALL.value) > -1) {
        return AppConstants.SELECT_ALL.name;
      }
      if (selected.indexOf(AppConstants.SELECT_NONE.value) > -1) {
        return AppConstants.SELECT_NONE.name;
      }
      return Array.isArray(selected) && selected?.length > 0 ? (
        <Typography className={classes.selectedTextStyle}>{`${selected.length} selected`}</Typography>
      ) : (
        <div className={classes.chips}>
          {(selected as string[]).map((value) => {
            const item: SelectItemProps =
              value === AppConstants.SELECT_ALL.value ? AppConstants.SELECT_ALL : items.filter((i) => i.value === value).length ? items.filter((i) => i.value === value)[0] : { name: "", value: "" };
            return <Chip key={value} label={item.name} size="small" className={classes.chip} />;
          })}
        </div>
      );
    } else {
      const item: SelectItemProps = items && items.filter((i) => i.value === selected).length ? items.filter((i) => i.value === selected)[0] : AppConstants.SELECT_NONE;
      return item.name;
    }
  };

  const close = () => {
    setIsOpen(false);
    if(!preserveSearchText) setSearchText("");
  };

  const stopImmediatePropagation = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <>
      <FormControl variant="outlined" className={classes.formControl} error={error} fullWidth={fullWidth}>
        {label && <InputLabel id={`${id}-${label}`}>{label}</InputLabel>}
        <Select
          {...rest}
          displayEmpty
          fullWidth={fullWidth}
          className={`${className || ""} ${isOpen ? "open " : ""}${label ? "hasLabel " : ""}`}
          ref={anchorEl}
          labelId={label ? `${id}-${label}` : ""}
          multiple={multiple}
          value={ddnValue}
          onOpen={() => {
            setIsOpen(true);
          }}
          onClose={close}
          onChange={handleDdnChange}
          renderValue={getChips}
          IconComponent={hideIconComponent ? () => <></> : downArrowIcon}
          MenuProps={{
            disablePortal: true,
            classes: {
              list: menuPropsList || classes.menuPropsList,
              paper: menuPaperProps || classes.menuPropsPaper,
            },
            autoFocus: false,
            anchorEl: anchorEl.current,
            getContentAnchorEl: null,
            anchorOrigin: anchorOrigin || anchorOriginDefault,
            MenuListProps: {
              ref: setMenuListRef,
            },
          }}
          disabled={disabled}
        >
          {enableSearch && (
            <ListSubheader className={`${classes.searchBox} ${customWidthSearchBar && classes.customWidthSearch}`} onClickCapture={stopImmediatePropagation} onKeyDown={(e) => e.stopPropagation()}>
              <TextField
                variant="outlined"
                size="small"
                value={searchText}
                autoFocus
                placeholder="Type to search..."
                fullWidth
                className="text-search"
                onChange={(e) => {
                  setSearchText(e.target.value);
                  if (onSearch) onSearch(e.target.value); // <-- Call API search if provided
                }}
                onKeyDown={(e) => {
                  if (e.key !== "Escape") {
                    e.stopPropagation();
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end" size="small">
                        <SvgIcon component={searchIcon} viewBox="0 0 16 16" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </ListSubheader>
          )}
          {(!searchText || searchText.length === 0) && multiple ? (
            <MenuItem className="selectAll" key={AppConstants.SELECT_ALL.value} value={AppConstants.SELECT_ALL.value}>
              <Checkbox size="small" color="primary" checked={ddnValue.indexOf(AppConstants.SELECT_ALL.value) > -1} />
              <ListItemText primary={AppConstants.SELECT_ALL.name} />
            </MenuItem>
          ) : (
            showSelectNone && (
              <MenuItem className="selectAll" key={AppConstants.SELECT_NONE.value} value={AppConstants.SELECT_NONE.value}>
                <ListItemText primary={AppConstants.SELECT_NONE.name} />
              </MenuItem>
            )
          )}
          {getMenuItems()}
          {/* {loading && (
    <MenuItem disabled>
      <CircularProgress size={24} />
    </MenuItem>
  )} */}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    </>
  );
};

export default SelectBox;
